import { useMutation } from "@tanstack/react-query";
import { AxiosError } from "axios";
import type { DateTime } from "luxon";
import { v4 } from "uuid";
import { useAnalytics } from "../contexts/analytics/hooks";
import { useDraftMode } from "../contexts/DraftModeContext";
import { useHospitalData } from "../contexts/HospitalDataProvider";
import { useRequestsContext } from "../contexts/RequestsContext";
import { useSelfScheduling } from "../contexts/SelfScheduling/SelfSchedulingContext";
import { useSnackbar } from "../contexts/SnackbarContext";
import { backendService } from "../services/backend.service";
import { trackEvent } from "../utils/analytics";
import {
	type AgencyPostingFailure,
	ChangesSource,
	ChangeType,
	type NurseInShift,
	type NurseInShiftCreationSource,
	type PublishRequestBody,
	type ShiftChange,
} from "../utils/models";
import { useInvalidateCallIns } from "./useInvalidateCallIns";
import { usePublishChanges } from "./usePublishChanges";

/** One nurse of a multi-add: a prepared shift (`prepareDataForAddNurse` + charge/training/job) and its grid slot. */
export type AddNursesEntry = {
	nurse: NurseInShift;
	shiftIndex: number;
	dayNo: number;
	weekIndex: number;
	location: string;
};

export type AddNursesParams = {
	entries: AddNursesEntry[];
	/** Every entry lands in the same bucket: one publish writes either scheduled or actual changes. */
	isActual: boolean;
	source: NurseInShiftCreationSource;
};

/** The `/staffing/publish` payload fields callers act on. Draft mode never reaches the server, so it only sets `shouldRefresh`. */
export type AddNursesResult = {
	shouldRefresh: boolean;
	cancelledPto?: { nurseId: number; preferenceId: string }[];
	agency_posting_failures?: AgencyPostingFailure[];
};

type InsertChangeArgs = {
	/** The prepared shift, with the caller's location and hours already laid over it. */
	nurse: NurseInShift;
	shiftIndex: number;
	dayNo: number;
	weekIndex: number;
	date: DateTime;
	/** Auto-scheduler departments land adds pending. */
	isPending: boolean;
};

/** The publish change that inserts one shift — the one shape both `addNurse` and `addNurses` send. */
const buildInsertChange = ({ nurse, shiftIndex, dayNo, weekIndex, date, isPending }: InsertChangeArgs): ShiftChange => ({
	nurse: { ...nurse, isPending },
	shiftIndex,
	shiftType: nurse.shiftType,
	weekIndex,
	dayIndex: dayNo,
	date,
	departmentId: nurse.departmentId,
	changeType: ChangeType.Insert,
});

type PublishBodyArgs = {
	changes: ShiftChange[];
	/** Every change lands in the same bucket: one publish writes either scheduled or actual changes. */
	isActual: boolean;
	source: NurseInShiftCreationSource;
	/** Request id → the nurse ids this add fills it with (`{}` when the add fills no request). */
	acceptedRequests: PublishRequestBody["accepted_requests"];
};

/** Files every change under one bucket: a single publish writes either scheduled or actual changes. */
const bucketChanges = (changes: ShiftChange[], isActual: boolean): Pick<PublishRequestBody, "changes" | "actual_changes"> => ({
	actual_changes: isActual ? changes : [],
	changes: isActual ? [] : changes,
});

/** The `/staffing/publish` body for a Staffing-page add. */
const buildPublishBody = ({
	changes,
	isActual,
	source,
	acceptedRequests,
}: PublishBodyArgs): PublishRequestBody & { creationSource: NurseInShiftCreationSource } => ({
	accepted_requests: acceptedRequests,
	declined_requests: {},
	...bucketChanges(changes, isActual),
	source: ChangesSource.Staffing,
	creationSource: source,
});

/** One shift a remove takes off the schedule, addressed by its grid slot. */
export type RemoveNursesEntry = {
	nurse: NurseInShift;
	shiftIndex: number;
	dayNo: number;
	weekIndex: number;
};

/** What the manager recorded about the removal. One set covers the whole batch: the footer asks once. */
export type RemovalDetails = {
	/** The full reason (`getFullReason()`); stored on the shift as the call-in message. */
	message?: string;
	/** The free-text note sent to the removed staff member. */
	removalMessage?: string;
	/** When the staff member called in — only for a call-in reason. */
	callInTime?: DateTime;
};

export type RemoveNursesParams = RemovalDetails & {
	entries: RemoveNursesEntry[];
	/** Every entry lands in the same bucket: one publish writes either scheduled or actual changes. */
	isActual: boolean;
	source: ChangesSource;
};

/** The `/staffing/publish` field callers act on. Draft mode never reaches the server, so it only sets `shouldRefresh`. */
export type RemoveNursesResult = { shouldRefresh: boolean };

/** A removal change carries the call-in time as the UTC ISO string the publish route stores, not a `DateTime`. */
type RemovalChange = Omit<ShiftChange, "nurse"> & { nurse: NurseInShift & { callInTime?: string | null } };

type RemovalChangeArgs = RemoveNursesEntry &
	RemovalDetails & {
		date: DateTime;
		/** The shift's own type, else its Day/Night bucket's. */
		shiftType: string;
		/**
		 * Auto-scheduler departments and a self-scheduling import period keep the shift and mark it pending-delete
		 * (an `Update`) for the manager to publish; everywhere else the removal is a real `Delete`.
		 */
		isPendingRemoval: boolean;
	};

/** The publish change that removes one shift — the one shape both `removeNurse` and `removeNurses` send. */
const buildRemovalChange = ({
	nurse,
	shiftIndex,
	dayNo,
	weekIndex,
	date,
	shiftType,
	isPendingRemoval,
	message,
	removalMessage,
	callInTime,
}: RemovalChangeArgs): RemovalChange => ({
	nurse: {
		...nurse,
		isPending: isPendingRemoval,
		isPendingToDelete: isPendingRemoval,
		message,
		removalMessage,
		callInTime: callInTime?.toUTC().toISO(),
	},
	shiftIndex,
	shiftType,
	weekIndex,
	dayIndex: dayNo,
	date,
	departmentId: nurse.departmentId,
	changeType: isPendingRemoval ? ChangeType.Update : ChangeType.Delete,
});

/** The `/staffing/publish` body for a removal — no request is accepted or declined by it. */
const buildRemovalPublishBody = ({
	changes,
	isActual,
	source,
}: {
	changes: RemovalChange[];
	isActual: boolean;
	source: ChangesSource;
}): PublishRequestBody => ({
	accepted_requests: {},
	declined_requests: {},
	...bucketChanges(changes, isActual),
	source,
});

export function useHandleNurseChange() {
	const { getShiftDate, refreshDepartmentData, chosenDepartment, hospitalData, isAutoScheduler, showErrorSnackbar, isHospital } = useHospitalData();
	const { showSnackbar } = useSnackbar();
	const { refreshRequests } = useRequestsContext();
	const { isDraftMode } = useDraftMode();
	const { isDataInSelfSchedulingPeriod } = useSelfScheduling();
	const { mutateAsync: publishChanges } = usePublishChanges();
	const { track, trackError } = useAnalytics();
	const invalidateCallIns = useInvalidateCallIns();

	/** Resolves the slot-dependent parts of a removal (date, shift type, pending rule) the same way for every caller. */
	const toRemovalChange = (entry: RemoveNursesEntry, details: RemovalDetails): RemovalChange => {
		const { nurse, shiftIndex, dayNo, weekIndex } = entry;
		const isImportingSelfScheduling = isDataInSelfSchedulingPeriod(hospitalData?.weeks[weekIndex]?.weeklyShifts[shiftIndex]?.days[dayNo]?.date);
		return buildRemovalChange({
			...entry,
			...details,
			date: getShiftDate(weekIndex, shiftIndex, dayNo),
			shiftType: nurse.shiftType ?? hospitalData?.weeks[weekIndex]?.weeklyShifts[shiftIndex]?.type,
			isPendingRemoval: isAutoScheduler || isImportingSelfScheduling,
		});
	};

	const {
		mutateAsync: addNurse,
		isPending: isAddingNurse,
		error: addNurseError,
	} = useMutation({
		mutationFn: async ({ nurse, shiftIndex, dayNo, weekIndex, location, requestId, isActual, shiftTimes, date, source }: any) => {
			// If draft mode is enabled, skip backend call (auto-tracking will detect the change)
			if (isDraftMode) {
				return { shouldRefresh: false };
			}

			const change = buildInsertChange({
				nurse: { ...nurse, location, ...shiftTimes },
				shiftIndex,
				dayNo,
				weekIndex,
				date: date ?? getShiftDate(weekIndex, shiftIndex, dayNo),
				isPending: isAutoScheduler,
			});
			const updateRequestBody = buildPublishBody({
				changes: [change],
				isActual: !!isActual,
				source,
				acceptedRequests: requestId ? { [requestId]: [nurse.nurseId] } : {},
			});

			if (!isDraftMode) {
				const response = await publishChanges(updateRequestBody);

				if (response.shouldRefresh) {
					refreshDepartmentData(chosenDepartment.department_id, false);
				}

				return response;
			}
			return { shouldRefresh: false };
		},
	});

	const {
		mutateAsync: addNurses,
		isPending: isAddingNurses,
		error: addNursesError,
	} = useMutation({
		mutationFn: async ({ entries, isActual, source }: AddNursesParams): Promise<AddNursesResult> => {
			// Draft mode: no backend call — auto-tracking picks up the local adds, exactly like addNurse.
			if (isDraftMode) {
				return { shouldRefresh: false };
			}
			if (entries.length === 0) {
				throw new Error("addNurses called without any nurses to add");
			}

			// One Insert per nurse, built exactly like `addNurse`'s, so N adds commit in ONE publish transaction:
			// all-or-nothing on the backend, which is why callers must exclude hard conflicts up front.
			const changes = entries.map(({ nurse, shiftIndex, dayNo, weekIndex, location }) =>
				buildInsertChange({
					nurse: { ...nurse, location },
					shiftIndex,
					dayNo,
					weekIndex,
					date: getShiftDate(weekIndex, shiftIndex, dayNo),
					isPending: isAutoScheduler,
				}),
			);
			const updateRequestBody = buildPublishBody({ changes, isActual, source, acceptedRequests: {} });

			const response: AddNursesResult = await publishChanges(updateRequestBody);
			if (response.shouldRefresh) {
				refreshDepartmentData(chosenDepartment.department_id, false);
			}
			return response;
		},
	});

	const {
		mutateAsync: moveNurse,
		isPending: isMovingNurse,
		error: moveNurseError,
	} = useMutation({
		mutationFn: async ({
			nurse,
			shiftIndex,
			dayNo,
			weekIndex,
			location,
			requestId,
			isActual,
			shiftTimes,
			date,
			otherDayNo,
			otherShiftNo,
			otherShiftTimes,
		}: any) => {
			const newNurseId = v4();

			const changes = [
				{
					nurse: {
						...nurse,
						isPending: isHospital,
						isPendingToDelete: isHospital,
						originalDay: otherShiftTimes.start.weekday % 7,
						originalShift: shiftIndex,
						...shiftTimes,
					},
					shiftIndex,
					shiftType: nurse.shiftType ?? hospitalData?.weeks[weekIndex]?.weeklyShifts[shiftIndex]?.type,
					weekIndex,
					dayIndex: dayNo,
					date: getShiftDate(weekIndex, shiftIndex, dayNo),
					departmentId: nurse.departmentId,
					changeType: ChangeType.Update,
				},
				{
					nurse: {
						...nurse,
						id: newNurseId,
						isPending: isHospital,
						location,
						startTime: otherShiftTimes.start,
						endTime: otherShiftTimes.end,
						originalDay: dayNo,
						originalShift: shiftIndex,
						isPendingToDelete: false,
					},
					shiftIndex: otherShiftNo,
					shiftType: nurse.shiftType,
					weekIndex,
					dayIndex: otherDayNo,
					date: date ?? getShiftDate(weekIndex, otherShiftNo, otherDayNo),
					departmentId: nurse.departmentId,
					changeType: ChangeType.Insert,
				},
			];

			const updateRequestBody = {
				accepted_requests: {},
				declined_requests: {},
				actual_changes: [],
				changes: [],
				source: ChangesSource.Staffing,
			};

			if (isActual) {
				updateRequestBody.actual_changes = changes as any;
			} else {
				updateRequestBody.changes = changes as any;
			}

			if (requestId) {
				updateRequestBody.accepted_requests = {
					[requestId]: [nurse.nurseId],
				};
			}

			if (!isDraftMode) {
				await publishChanges(updateRequestBody);
			}

			return newNurseId;
		},
	});

	const {
		mutateAsync: swapNurses,
		isPending: isSwappingNurses,
		error: swapNursesError,
	} = useMutation({
		mutationFn: async ({ originShiftId, targetShiftId }: { originShiftId: string; targetShiftId: string }) => {
			const response = await backendService.createAdminSwapShiftRequest(chosenDepartment.department_id, {
				originShiftId,
				targetShiftId,
			});

			if (response.data.shouldRefresh) {
				refreshDepartmentData(chosenDepartment.department_id, false);
			}

			return response.data;
		},
		onSuccess: (_data, variables) => {
			track("admin_swap_submitted", {
				origin_shift_id: variables.originShiftId,
				target_shift_id: variables.targetShiftId,
				department_id: chosenDepartment.department_id,
			});
		},
		onError: (error) => {
			let message = "Swap nurses failed";
			if (error instanceof AxiosError) {
				const detail = error.response?.data?.detail;
				message = (typeof detail === "string" ? detail : detail?.message) || message;
			}
			showErrorSnackbar(message, {}, error);
		},
	});

	const {
		mutateAsync: swapBetweenShifts,
		isPending: isSwappingBetweenShifts,
		error: swapBetweenShiftsError,
	} = useMutation({
		mutationFn: async ({ originShiftId, targetShiftId }: { originShiftId: string; targetShiftId: string }) => {
			const response = await backendService.createSwapBetweenRequest(chosenDepartment.department_id, {
				originShiftId,
				targetShiftId,
			});
			if (response.data.shouldRefresh) {
				refreshDepartmentData(chosenDepartment.department_id, false);
			}
			return response.data;
		},
		onSuccess: async (_data, variables) => {
			track("bedside_swap_requested", {
				origin_shift_id: variables.originShiftId,
				target_shift_id: variables.targetShiftId,
				department_id: chosenDepartment.department_id,
			});
			showSnackbar("Swap request created successfully", "success");
			await refreshRequests();
		},
		onError: (error) => {
			let message = "Swap between shifts failed";
			if (error instanceof AxiosError) {
				const detail = error.response?.data?.detail;
				message = (typeof detail === "string" ? detail : detail?.message) || message;
			}
			showErrorSnackbar(message, {}, error);
		},
	});

	const {
		mutateAsync: updateNurse,
		isPending: isUpdatingNurse,
		error: updateNurseError,
	} = useMutation({
		mutationFn: async ({
			nurse,
			shiftIndex,
			dayNo,
			weekIndex,
			isActual,
		}: {
			nurse: Partial<NurseInShift>;
			shiftIndex: number;
			dayNo: number;
			weekIndex: number;
			isActual?: boolean;
		}) => {
			// If draft mode is enabled, skip backend call (auto-tracking will detect the change)
			if (isDraftMode) {
				return { shouldRefresh: false };
			}

			const changes = [
				{
					nurse: {
						...nurse,
						isPending: isHospital,
						isPendingToDelete: isHospital,
					} as NurseInShift,
					shiftIndex,
					shiftType: chosenDepartment.shift_types[shiftIndex],
					weekIndex,
					dayIndex: dayNo,
					date: getShiftDate(weekIndex, shiftIndex, dayNo),
					departmentId: nurse.departmentId as string,
					changeType: ChangeType.Update,
				},
			];

			const updateRequestBody = {
				accepted_requests: {},
				declined_requests: {},
				actual_changes: isActual ? changes : [],
				changes: isActual ? [] : changes,
				source: ChangesSource.Staffing,
			};

			if (!isDraftMode) {
				const response = await publishChanges(updateRequestBody);

				if (response.shouldRefresh) {
					refreshDepartmentData(chosenDepartment.department_id, false);
				}
				trackEvent("update_nurse_in_shift", { nurse, shiftIndex, dayNo, weekIndex });
				return response;
			}
			trackEvent("update_nurse_in_shift", { nurse, shiftIndex, dayNo, weekIndex });
			return { shouldRefresh: false };
		},
		onError: (error, variables) => {
			if (error instanceof AxiosError) {
				showErrorSnackbar("nurse_in_shift_update_failed", { nurseId: variables.nurse?.id }, error);
			} else {
				showErrorSnackbar("nurse_in_shift_update_failed", {
					nurseId: variables.nurse?.id,
				});
			}
		},
	});

	const {
		mutateAsync: discardNurseMove,
		isPending: isDiscarding,
		error: discardNurseError,
	} = useMutation({
		mutationFn: async ({ nurse, shiftIndex, dayNo, weekIndex, location, isActual, shiftTimes, date, otherShiftTimes, otherNurse, shiftType }: any) => {
			let changes: any[] = [];

			if (nurse.isPendingToDelete) {
				changes = [
					{
						nurse: {
							...nurse,
							isPending: false,
							isPendingToDelete: false,
							isPendingToMove: false,
							isPublished: true,
						},
						shiftIndex,
						shiftType,
						weekIndex,
						dayIndex: dayNo,
						date: getShiftDate(weekIndex, shiftIndex, dayNo),
						departmentId: nurse.departmentId,
						changeType: ChangeType.Update,
					},
					{
						nurse: {
							...otherNurse,
							isPending: false,
							isPendingToDelete: false,
							isPublished: false,
							isPendingToMove: false,
							location,
							originalDay: dayNo,
							originalShift: shiftIndex,
							...shiftTimes,
						},
						shiftIndex,
						shiftType,
						weekIndex,
						dayIndex: dayNo,
						date: getShiftDate(weekIndex, shiftIndex, otherNurse.originalDay),
						departmentId: nurse.departmentId,
						changeType: ChangeType.Delete,
					},
				];
			} else {
				changes = [
					{
						nurse: {
							...nurse,
							isPending: false,
							isPendingToDelete: false,
							isPublished: false,
							isPendingToMove: false,
							location,
							originalDay: dayNo,
							originalShift: shiftIndex,
							...shiftTimes,
						},
						shiftIndex,
						shiftType,
						weekIndex,
						dayIndex: dayNo,
						date: getShiftDate(weekIndex, shiftIndex, dayNo),
						departmentId: nurse.departmentId,
						changeType: ChangeType.Delete,
					},
					{
						nurse: {
							...otherNurse,
							isPending: false,
							isPendingToDelete: false,
							isPendingToMove: false,
							isPublished: true,
							location,
							originalDay: dayNo,
							originalShift: shiftIndex,
							startTime: otherShiftTimes.start,
							endTime: otherShiftTimes.end,
						},
						shiftIndex,
						shiftType,
						weekIndex,
						dayIndex: nurse.originalDay,
						date: getShiftDate(weekIndex, shiftIndex, nurse.originalDay),
						departmentId: nurse.departmentId,
						changeType: ChangeType.Update,
					},
				];
			}

			const updateRequestBody = {
				accepted_requests: {},
				declined_requests: {},
				actual_changes: [],
				changes: [],
				source: ChangesSource.Staffing,
			};

			if (isActual) {
				updateRequestBody.actual_changes = changes as any;
			} else {
				updateRequestBody.changes = changes as any;
			}

			if (!isDraftMode) {
				const response = await publishChanges(updateRequestBody);
				return response;
			}
			return { shouldRefresh: false };
		},
	});

	const { mutateAsync: discardNurseRemove } = useMutation({
		mutationFn: async ({ nurse, shiftIndex, weekIndex, isActual, dayNo, shiftType }: any) => {
			const isImportingSelfScheduling = isDataInSelfSchedulingPeriod(hospitalData?.weeks[weekIndex]?.weeklyShifts[shiftIndex]?.days[dayNo]?.date);
			const changes: any[] = [
				{
					nurse: {
						...nurse,
						isPendingToDelete: false,
						isPending: isImportingSelfScheduling,
					},
					shiftIndex,
					shiftType,
					weekIndex,
					dayIndex: dayNo,
					date: getShiftDate(weekIndex, shiftIndex, dayNo),
					departmentId: nurse.departmentId,
					changeType: ChangeType.Update,
				},
			];

			const updateRequestBody = {
				accepted_requests: {},
				declined_requests: {},
				actual_changes: [],
				changes: [],
				source: ChangesSource.Staffing,
			};

			if (isActual) {
				updateRequestBody.actual_changes = changes as any;
			} else {
				updateRequestBody.changes = changes as any;
			}

			if (!isDraftMode) {
				const response = await publishChanges(updateRequestBody);
				return response;
			}
			return { shouldRefresh: false };
		},
	});

	const { mutateAsync: discardNurseAdd } = useMutation({
		mutationFn: async ({ nurse, shiftIndex, weekIndex, isActual, dayNo, shiftType }: any) => {
			const isImportingSelfScheduling = isDataInSelfSchedulingPeriod(hospitalData?.weeks[weekIndex]?.weeklyShifts[shiftIndex]?.days[dayNo]?.date);
			const changes: any[] = [
				{
					nurse: {
						...nurse,
						isPendingToDelete: isImportingSelfScheduling,
					},
					shiftIndex,
					shiftType,
					weekIndex,
					dayIndex: dayNo,
					date: getShiftDate(weekIndex, shiftIndex, dayNo),
					departmentId: nurse.departmentId,
					changeType: isImportingSelfScheduling ? ChangeType.Update : ChangeType.Delete,
				},
			];

			const updateRequestBody = {
				accepted_requests: {},
				declined_requests: {},
				actual_changes: [],
				changes: [],
				source: ChangesSource.Staffing,
			};

			if (isActual) {
				updateRequestBody.actual_changes = changes as any;
			} else {
				updateRequestBody.changes = changes as any;
			}

			if (!isDraftMode) {
				const response = await publishChanges(updateRequestBody);
				return response;
			}
			return { shouldRefresh: false };
		},
	});

	const {
		mutateAsync: removeNurse,
		isPending: isRemovingNurse,
		error: removeNurseError,
	} = useMutation({
		mutationFn: async ({ nurse, shiftIndex, dayNo, weekIndex, isActual, message, removalMessage, source, callInTime }: any) => {
			// If draft mode is enabled, skip backend call (auto-tracking will detect the change)
			if (isDraftMode) {
				return { shouldRefresh: false };
			}

			const change = toRemovalChange({ nurse, shiftIndex, dayNo, weekIndex }, { message, removalMessage, callInTime });
			const updateRequestBody = buildRemovalPublishBody({
				changes: [change],
				isActual: !!isActual,
				// DEFAULT: the single remove predates the explicit source; every Staffing-page caller omits it.
				source: source ?? ChangesSource.Staffing,
			});

			const response = await publishChanges(updateRequestBody);
			if (response.shouldRefresh) {
				refreshDepartmentData(chosenDepartment.department_id, false);
			}
			return response;
		},
		// A removal is a call-in write: it is what puts a row in the call-ins list and a marker on the
		// profile calendar. Nothing else in this flow touches that cache, so without this the manager
		// who just did the removal is the one person who cannot see it. Draft mode made no backend
		// change, so there is nothing to re-read.
		onSuccess: () => {
			if (isDraftMode) return;
			void invalidateCallIns();
		},
	});

	/**
	 * Removes N shifts in ONE publish — each change built exactly like `removeNurse`'s, so the batch is one
	 * backend transaction: all-or-nothing. The caller owns the snackbar (it knows who was removed), as with
	 * `removeNurse`; a rejected publish rejects this promise.
	 */
	const {
		mutateAsync: removeNurses,
		isPending: isRemovingNurses,
		error: removeNursesError,
	} = useMutation({
		mutationFn: async ({ entries, isActual, source, message, removalMessage, callInTime }: RemoveNursesParams): Promise<RemoveNursesResult> => {
			// Draft mode: no backend call — auto-tracking picks up the local removals, exactly like removeNurse.
			if (isDraftMode) {
				return { shouldRefresh: false };
			}
			if (entries.length === 0) {
				throw new Error("removeNurses called without any shifts to remove");
			}

			const changes = entries.map((entry) => toRemovalChange(entry, { message, removalMessage, callInTime }));
			const response: RemoveNursesResult = await publishChanges(buildRemovalPublishBody({ changes, isActual, source }));
			if (response.shouldRefresh) {
				refreshDepartmentData(chosenDepartment.department_id, false);
			}
			return response;
		},
		// Same call-in cache as `removeNurse`: the removals are call-in writes.
		onSuccess: () => {
			if (isDraftMode) return;
			void invalidateCallIns();
		},
		// Recorded here so no failed batch goes untracked; the caller still owns the snackbar (it knows the names).
		onError: (error, { entries, isActual, source }) => {
			trackError("remove_nurses_error", {
				department_id: chosenDepartment.department_id,
				shift_ids: entries.map((entry) => entry.nurse.id).join(","),
				shift_count: entries.length,
				is_actual: isActual,
				source,
				error_message: error instanceof Error ? error.message : String(error),
			});
		},
	});

	const {
		mutateAsync: toggleNurseCharge,
		isPending: isTogglingNurseCharge,
		error: nurseChargeError,
	} = useMutation({
		mutationFn: async ({ nurse, shiftIndex, dayNo, weekIndex, isActual }: any) => {
			// If draft mode is enabled, skip backend call (auto-tracking will detect the change)
			if (isDraftMode) {
				return { shouldRefresh: false };
			}

			const changes = [
				{
					nurse: {
						...nurse,
						charge: !nurse.charge,
					},
					shiftIndex,
					shiftType: nurse.shiftType,
					weekIndex,
					dayIndex: dayNo,
					date: getShiftDate(weekIndex, shiftIndex, dayNo),
					departmentId: nurse.departmentId,
					changeType: ChangeType.Update,
				},
			];

			const updateRequestBody = {
				accepted_requests: {},
				declined_requests: {},
				actual_changes: [],
				changes: [],
				source: ChangesSource.Staffing,
			};

			if (isActual) {
				updateRequestBody.actual_changes = changes as any;
			} else {
				updateRequestBody.changes = changes as any;
			}

			if (!isDraftMode) {
				const response = await publishChanges(updateRequestBody);

				if (response.shouldRefresh) {
					refreshDepartmentData(chosenDepartment.department_id, false);
				}

				return response;
			}
			return { shouldRefresh: false };
		},
	});

	const {
		mutateAsync: setNurseTrainingStatus,
		isPending: isLoadingTrainingStatus,
		error: trainingStatusError,
	} = useMutation({
		mutationFn: async ({ shifts, shiftsActual, shiftIndex, dayNo, weekIndex, trainingStatus }: any) => {
			// If draft mode is enabled, skip backend call (auto-tracking will detect the change)
			if (isDraftMode) {
				return { shouldRefresh: false };
			}
			const isImportingSelfScheduling = isDataInSelfSchedulingPeriod(hospitalData?.weeks[weekIndex]?.weeklyShifts[shiftIndex]?.days[dayNo]?.date);

			const changes = shifts.map((nurse: NurseInShift) => ({
				nurse: {
					...nurse,
					trainingStatus,
					isPending: isImportingSelfScheduling,
				},
				shiftIndex,
				shiftType: nurse.shiftType,
				weekIndex,
				dayIndex: dayNo,
				date: getShiftDate(weekIndex, shiftIndex, dayNo),
				departmentId: nurse.departmentId,
				changeType: ChangeType.Update,
			}));

			const changesActual = shiftsActual.map((nurse: NurseInShift) => ({
				nurse: {
					...nurse,
					trainingStatus,
					isPending: false,
				},
				shiftIndex,
				shiftType: nurse.shiftType,
				weekIndex,
				dayIndex: dayNo,
				date: getShiftDate(weekIndex, shiftIndex, dayNo),
				departmentId: nurse.departmentId,
				changeType: ChangeType.Update,
			}));

			const updateRequestBody = {
				accepted_requests: {},
				declined_requests: {},
				actual_changes: changesActual,
				changes: changes,
				source: ChangesSource.Staffing,
			};

			if (!isDraftMode) {
				const response = await publishChanges(updateRequestBody);

				if (response.shouldRefresh) {
					refreshDepartmentData(chosenDepartment.department_id, false);
				}

				return response;
			}
			return { shouldRefresh: false };
		},
	});

	return {
		addNurse,
		addNurses,
		removeNurse,
		removeNurses,
		toggleNurseCharge,
		discardNurseAdd,
		setNurseTrainingStatus,
		moveNurse,
		swapNurses,
		swapBetweenShifts,
		discardNurseMove,
		discardNurseRemove,
		updateNurse,
		isLoading: {
			addNurse: isAddingNurse,
			addNurses: isAddingNurses,
			removeNurse: isRemovingNurse,
			removeNurses: isRemovingNurses,
			toggleNurseCharge: isTogglingNurseCharge,
			setNurseTrainingStatus: isLoadingTrainingStatus,
			moveNurse: isMovingNurse,
			swapNurses: isSwappingNurses,
			swapBetweenShifts: isSwappingBetweenShifts,
			discardNurseMove: isDiscarding,
			updateNurse: isUpdatingNurse,
		},
		error: {
			addNurse: addNurseError,
			addNurses: addNursesError,
			removeNurse: removeNurseError,
			removeNurses: removeNursesError,
			toggleNurseCharge: nurseChargeError,
			setNurseTrainingStatus: trainingStatusError,
			moveNurse: moveNurseError,
			swapNurses: swapNursesError,
			swapBetweenShifts: swapBetweenShiftsError,
			discardNurseMove: discardNurseError,
			updateNurse: updateNurseError,
		},
	};
}
